import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import {
  Clock, Coins, Sparkle, Users, ArrowLeft, ShieldCheck,
  Lightning, ChartLineUp, CheckCircle, XCircle, ArrowsCounterClockwise,
  Robot, Hourglass,
} from "@phosphor-icons/react";

const STATUS_TEXT = {
  open: "Open", applied: "Applied", assigned: "Assigned", in_progress: "In Progress",
  review: "In Review", completed: "Completed", scored: "Closed",
  closed: "Closed", rework_required: "Rework needed", blocked: "Blocked",
  draft: "Draft", pending_approval: "Pending approval", cancelled: "Cancelled",
};

export default function TaskDetail() {
  const { task_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [recs, setRecs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");
  const [agents, setAgents] = useState([]);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [agentDeadline, setAgentDeadline] = useState("");
  const [rerunFeedback, setRerunFeedback] = useState("");
  const [reviewForm, setReviewForm] = useState({
    decision: "approved", quality_score: 85, speed_score: 85, collaboration_score: 85,
    innovation_score: 75, notes: "",
  });

  const load = async () => {
    const { data } = await api.get(`/tasks/${task_id}`);
    setBundle(data);
  };
  useEffect(() => { load(); }, [task_id]); // eslint-disable-line

  const task = bundle?.task;
  const isOwner = task && user.user_id === task.owner_id;
  const isCreator = task && user.user_id === task.creator_id;
  const isAssignee = task && (task.assignee_ids || []).includes(user.user_id);
  const isReviewer = task && (user.user_id === task.reviewer_id || user.role === "ADMIN");
  const hasApplied = bundle?.applications.some((a) => a.user_id === user.user_id);
  const deliverables = bundle?.deliverables || [];
  const pickupPending = (bundle?.pickup_approvals || []).length > 0;

  const loadRecs = async () => {
    try {
      const { data } = await api.get(`/tasks/${task_id}/recommend-contributors`);
      setRecs(data);
    } catch {}
  };
  useEffect(() => { if (isOwner) loadRecs(); }, [isOwner, task_id]); // eslint-disable-line

  useEffect(() => {
    if (isOwner || isCreator) {
      api.get("/agents").then(({ data }) => setAgents(data)).catch(() => {});
    }
  }, [isOwner, isCreator]); // eslint-disable-line

  const handleApply = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task_id}/apply`, { message: applyMsg });
      toast.success("Request submitted — awaiting manager & creator approval");
      setApplyMsg("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const handleSelfPick = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task_id}/self-pick`);
      toast.success("Pick-up requested — awaiting manager & creator approval");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const assignAgent = async () => {
    if (!selectedAgent) return toast.error("Pick an agent first.");
    setBusy(true);
    try {
      await api.post(`/tasks/${task_id}/assign-agent`, { agent_id: selectedAgent, deadline: agentDeadline || null });
      toast.success("Agent is working on it — deliverable generated");
      setAgentDialogOpen(false);
      setSelectedAgent("");
      setAgentDeadline("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const decideDeliverable = async (deliverableId, decision, notes) => {
    setBusy(true);
    try {
      await api.post(`/deliverables/${deliverableId}/decide`, { decision, notes });
      toast.success(decision === "close" ? "Task closed — deliverable accepted" : "Re-running agent with your feedback…");
      setRerunFeedback("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task_id}/status`, { status });
      toast.success(`Moved to ${STATUS_TEXT[status] || status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitForReview = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task_id}/submit`);
      toast.success("Submitted for review");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task_id}/review`, {
        ...reviewForm, task_id, reviewer_id: user.user_id,
      });
      toast.success(`Review submitted (${data.status})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!task) return <div className="p-8 text-sm text-zinc-500" data-testid="task-loading">Loading…</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-testid="task-detail-page">
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1 hover:text-foreground" data-testid="task-back-btn">
        <ArrowLeft size={12} /> Back
      </button>

      <div className="border border-border p-6 bg-background space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{task.category || "Task"}</div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mt-1" data-testid="task-title">{task.title}</h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status</div>
            <div className="text-sm font-semibold mt-1" data-testid="task-status">{STATUS_TEXT[task.status] || task.status}</div>
          </div>
        </div>
        <p className="text-base leading-relaxed text-zinc-700 dark:text-zinc-300" data-testid="task-description">{task.description}</p>
        {task.ai_summary && (
          <div className="border-l-2 border-[#E1252A] pl-3 text-sm text-zinc-600 dark:text-zinc-400 italic">
            AI summary: {task.ai_summary}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 pt-3 border-t border-border">
          <Stat icon={Clock} label="Effort" value={`${task.effort_hours}h`} />
          <Stat icon={Lightning} label="Priority" value={task.priority} />
          <Stat icon={Sparkle} label="Reward" value={`${task.reward_points} pts`} />
          <Stat icon={Coins} label="Credits" value={task.credit_cost} accent />
          <Stat icon={ChartLineUp} label="Complexity" value={task.complexity} />
        </div>

        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-border">
          {(task.required_skills || []).map((s) => (
            <Badge key={s} variant="outline" className="rounded-sm font-mono text-[10px] uppercase tracking-wider">{s}</Badge>
          ))}
        </div>
      </div>

      {/* Action zone */}
      <div className="border border-border p-6 bg-surface/40 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Actions</div>
        <div className="flex flex-wrap gap-2">
          {/* Apply / self-pick */}
          {!isOwner && !isAssignee && (task.status === "open" || task.status === "applied") && !hasApplied && (
            <>
              {task.effort_hours <= 8 && (
                <Button onClick={handleSelfPick} disabled={busy} className="rounded-sm" data-testid="self-pick-btn">
                  Self-pick (≤8h)
                </Button>
              )}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-sm" data-testid="open-apply-dialog-btn">Apply</Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm">
                  <DialogHeader><DialogTitle>Apply to "{task.title}"</DialogTitle></DialogHeader>
                  <Textarea
                    placeholder="A short pitch — why you?"
                    value={applyMsg}
                    onChange={(e) => setApplyMsg(e.target.value)}
                    rows={4}
                    className="rounded-sm"
                    data-testid="apply-message-input"
                  />
                  <DialogFooter>
                    <Button onClick={handleApply} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="submit-apply-btn">
                      Submit application
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
          {hasApplied && !isAssignee && (
            <span className="text-sm text-zinc-500" data-testid="already-applied-msg">You've already applied.</span>
          )}
          {/* Assignee actions */}
          {isAssignee && task.status === "assigned" && (
            <Button onClick={() => updateStatus("in_progress")} className="rounded-sm" data-testid="start-work-btn">Start work</Button>
          )}
          {isAssignee && task.status === "in_progress" && (
            <Button onClick={submitForReview} className="rounded-sm" data-testid="submit-review-btn">Mark Complete</Button>
          )}
          {isAssignee && task.status === "rework_required" && (
            <Button onClick={() => updateStatus("in_progress")} className="rounded-sm" data-testid="resume-work-btn">Resume work</Button>
          )}

          {/* Reviewer actions */}
          {isReviewer && task.status === "review" && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-foreground text-background" data-testid="open-review-dialog-btn">Score & decide</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-md">
                <DialogHeader><DialogTitle>Review submission</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  {[
                    ["quality_score", "Quality"],
                    ["speed_score", "Speed"],
                    ["collaboration_score", "Collaboration"],
                    ["innovation_score", "Innovation"],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="uppercase tracking-wider">{label}</span>
                        <span className="font-mono">{reviewForm[k]}</span>
                      </div>
                      <Slider
                        value={[reviewForm[k]]}
                        onValueChange={(v) => setReviewForm((f) => ({ ...f, [k]: v[0] }))}
                        max={100} step={1}
                        data-testid={`review-${k}-slider`}
                      />
                    </div>
                  ))}
                  <Textarea
                    placeholder="Notes…"
                    value={reviewForm.notes}
                    onChange={(e) => setReviewForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={3}
                    className="rounded-sm"
                    data-testid="review-notes-input"
                  />
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => { setReviewForm((f) => ({ ...f, decision: "rework" })); submitReview(); }} disabled={busy} className="rounded-sm" data-testid="review-rework-btn">
                    <ArrowsCounterClockwise size={14} className="mr-1" /> Not complete (rework)
                  </Button>
                  <Button variant="outline" onClick={() => { setReviewForm((f) => ({ ...f, decision: "rejected" })); submitReview(); }} disabled={busy} className="rounded-sm" data-testid="review-reject-btn">
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                  <Button onClick={() => { setReviewForm((f) => ({ ...f, decision: "approved" })); submitReview(); }} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="review-approve-btn">
                    <CheckCircle size={14} className="mr-1" /> Close & accept
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* AI agent assignment (creator/owner) */}
          {(isOwner || isCreator) && !["completed", "scored", "closed", "cancelled"].includes(task.status) && (
            <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="assign-agent-btn">
                  <Robot size={14} className="mr-1" /> Assign to AI agent
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-lg" data-testid="assign-agent-dialog">
                <DialogHeader><DialogTitle>Assign an AI agent</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-zinc-500">Pick an agent and a timeline. The agent will execute the task and produce a deliverable automatically.</p>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {agents.length === 0 && <div className="text-sm text-zinc-500">No agents available. Ask an admin to add one.</div>}
                    {agents.map((a) => (
                      <button
                        key={a.agent_id}
                        onClick={() => a.availability !== "busy" && setSelectedAgent(a.agent_id)}
                        disabled={a.availability === "busy"}
                        className={`w-full text-left border p-3 rounded-sm transition-colors ${a.availability === "busy" ? "opacity-50 cursor-not-allowed border-border" : selectedAgent === a.agent_id ? "border-[#E1252A] bg-[#E1252A]/5" : "border-border hover:bg-zinc-50 dark:hover:bg-zinc-900"}`}
                        data-testid={`agent-option-${a.agent_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Robot size={16} className="text-[#E1252A]" />
                          <span className="font-semibold">{a.name}</span>
                          <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${a.availability === "available" ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}`}>{a.availability}</Badge>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">{a.description}</div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(a.capabilities || []).map((c) => (
                            <Badge key={c} variant="outline" className="rounded-sm font-mono text-[9px] uppercase">{c}</Badge>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Timeline / deadline</div>
                    <Input type="datetime-local" value={agentDeadline} onChange={(e) => setAgentDeadline(e.target.value)} className="rounded-sm" data-testid="agent-deadline-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={assignAgent} disabled={busy || !selectedAgent} className="rounded-sm bg-foreground text-background" data-testid="confirm-assign-agent-btn">
                    {busy ? "Running agent…" : "Assign & run"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {pickupPending && (
            <span className="inline-flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400" data-testid="pickup-pending-note">
              <Hourglass size={14} /> A pick-up request is awaiting approval.
            </span>
          )}
        </div>
      </div>

      {/* AI Deliverables */}
      {deliverables.length > 0 && (
        <div className="border border-border bg-background" data-testid="deliverables-panel">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Robot size={18} className="text-[#E1252A]" />
            <div className="text-sm font-semibold">AI Deliverables</div>
          </div>
          <div className="divide-y divide-border">
            {deliverables.map((d) => (
              <div key={d.deliverable_id} className="p-6 space-y-3" data-testid={`deliverable-${d.deliverable_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{d.agent_name}</span>
                    <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{d.status}</Badge>
                    {d.deadline && <span className="text-xs text-zinc-500">due {new Date(d.deadline).toLocaleDateString()}</span>}
                  </div>
                  {(isOwner || isCreator) && d.status === "ready" && (
                    <div className="flex items-center gap-2">
                      <Dialog onOpenChange={(o) => { if (!o) setRerunFeedback(""); }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" disabled={busy} className="rounded-sm" data-testid={`deliverable-rerun-${d.deliverable_id}`}>
                            <ArrowsCounterClockwise size={14} className="mr-1" /> Send back / re-run
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm" data-testid={`rerun-dialog-${d.deliverable_id}`}>
                          <DialogHeader><DialogTitle>Send back to {d.agent_name}</DialogTitle></DialogHeader>
                          <p className="text-sm text-zinc-500">Add feedback so the agent can refine its next attempt.</p>
                          <Textarea
                            placeholder="e.g. Make it more concise, add a risks section, focus on the EU market…"
                            value={rerunFeedback}
                            onChange={(e) => setRerunFeedback(e.target.value)}
                            rows={4}
                            className="rounded-sm"
                            data-testid={`rerun-feedback-${d.deliverable_id}`}
                          />
                          <DialogFooter>
                            <Button onClick={() => decideDeliverable(d.deliverable_id, "rerun", rerunFeedback)} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid={`confirm-rerun-${d.deliverable_id}`}>
                              {busy ? "Re-running…" : "Re-run with feedback"}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" onClick={() => decideDeliverable(d.deliverable_id, "close")} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid={`deliverable-close-${d.deliverable_id}`}>
                        <CheckCircle size={14} className="mr-1" /> Accept & close
                      </Button>
                    </div>
                  )}
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none border border-border rounded-sm p-4 bg-surface/40 overflow-x-auto" data-testid={`deliverable-output-${d.deliverable_id}`}>
                  <ReactMarkdown>{d.output || "_No output_"}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="applicants" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="applicants" data-testid="tab-applicants">Applicants ({bundle.applications.length})</TabsTrigger>
          {isOwner && <TabsTrigger value="recommendations" data-testid="tab-recommendations">AI Recommendations</TabsTrigger>}
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({bundle.reviews.length})</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="applicants" className="pt-4 space-y-2">
          {bundle.applications.length === 0 && <div className="text-sm text-zinc-500">No applications yet.</div>}
          {bundle.applications.map((a) => (
            <div key={a.app_id} className="border border-border p-4 flex items-center justify-between gap-3" data-testid={`applicant-${a.app_id}`}>
              <div className="min-w-0">
                <div className="font-medium">{a.user?.name}</div>
                <div className="text-xs text-zinc-500">{a.user?.role} · {a.user?.email}</div>
                {a.message && <div className="text-sm mt-1 text-zinc-700 dark:text-zinc-300">"{a.message}"</div>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-sm uppercase text-[10px]">{a.status}</Badge>
                {isOwner && a.status === "pending" && (
                  <span className="text-xs text-amber-600 dark:text-amber-400" data-testid={`applicant-pending-note-${a.app_id}`}>
                    In approval queue
                  </span>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        {isOwner && (
          <TabsContent value="recommendations" className="pt-4 space-y-2">
            {recs.length === 0 && <div className="text-sm text-zinc-500">No recommendations yet.</div>}
            {recs.map((r) => (
              <div key={r.user_id} className="border border-border p-4 grid grid-cols-12 items-center gap-3" data-testid={`rec-${r.user_id}`}>
                <div className="col-span-4">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-zinc-500">Reputation {r.reputation}</div>
                </div>
                <div className="col-span-2 text-xs uppercase tracking-wider text-zinc-500">Match</div>
                <div className="col-span-4">
                  <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#E1252A]" style={{ width: `${Math.min(100, r.score)}%` }} />
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-sm">{r.score}</div>
              </div>
            ))}
          </TabsContent>
        )}

        <TabsContent value="reviews" className="pt-4 space-y-2">
          {bundle.reviews.length === 0 && <div className="text-sm text-zinc-500">No reviews yet.</div>}
          {bundle.reviews.map((r) => (
            <div key={r.review_id} className="border border-border p-4 space-y-2" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Decision: {r.decision}</div>
                <div className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>Quality <span className="font-mono">{r.quality_score}</span></div>
                <div>Speed <span className="font-mono">{r.speed_score}</span></div>
                <div>Collab <span className="font-mono">{r.collaboration_score}</span></div>
                <div>Innov <span className="font-mono">{r.innovation_score}</span></div>
              </div>
              {r.notes && <div className="text-sm text-zinc-600 dark:text-zinc-400">{r.notes}</div>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="team" className="pt-4 space-y-2">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Owner</div>
          <div className="border border-border p-4">{bundle.owner?.name} · {bundle.owner?.role}</div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 pt-2">Assignees</div>
          {bundle.assignees.length === 0 && <div className="text-sm text-zinc-500">No one assigned yet.</div>}
          {bundle.assignees.map((a) => (
            <div key={a.user_id} className="border border-border p-4">{a.name} · {a.role}</div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-zinc-500"><Icon size={12} /><span className="text-[10px] uppercase tracking-wider">{label}</span></div>
      <div className={`text-base font-mono mt-0.5 ${accent ? "text-[#E1252A] font-semibold" : ""}`}>{value}</div>
    </div>
  );
}
